import React, { useState } from 'react';
import {
  Box,
  Grid,
  Typography,
  TextField,
  Card,
  CardMedia,
  CardContent,
  IconButton,
  Link as MuiLink,
} from '@mui/material';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import LinkIcon from '@mui/icons-material/Link';
import ivf1 from '../../../../../assets/images/ivf1.png';
import ivf2 from '../../../../../assets/images/ivf2.png';
import embryology from '../../../../../assets/images/embryology.png';
import surrogacy from '../../../../../assets/images/surrogacy.png';

import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import { useTranslation } from 'react-i18next';

const videos = [
  {
    title: 'In Vitro Fertilization',
    description: 'How in vitro fertilization (IVF) works - Nassim Assefi and Brian A. Levine',
    thumbnail: ivf1,
    progress: 0.7,
    link: 'https://www.youtube.com/embed/_ijBi93_I94',
  },
  {
    title: 'Embryology',
    description: 'How in vitro fertilization (IVF) works - Nassim Assefi and Brian A. Levine',
    thumbnail: embryology,
    progress: 0.6,
    link: 'https://www.youtube.com/embed/MPc1nuDr1f0',
  },
  {
    title: 'In Vitro Fertilization',
    description: 'How in vitro fertilization (IVF) works - Nassim Assefi and Brian A. Levine',
    thumbnail: ivf2,
    progress: 0.4,
    link: 'https://www.youtube.com/embed/_ijBi93_I94',
  },
  {
    title: 'Surrogacy',
    description: 'How in vitro fertilization (IVF) works - Nassim Assefi and Brian A. Levine',
    thumbnail: surrogacy,
    progress: 0.3,
    link: 'https://www.youtube.com/embed/MPc1nuDr1f0',
  },
  // Repeat for demo
  {
    title: 'Embryology',
    description: 'How in vitro fertilization (IVF) works - Nassim Assefi and Brian A. Levine',
    thumbnail: embryology,
    progress: 0.6,
    link: 'https://www.youtube.com/embed/_ijBi93_I94',
  },
  {
    title: 'Surrogacy',
    description: 'How in vitro fertilization (IVF) works - Nassim Assefi and Brian A. Levine',
    thumbnail: surrogacy,
    progress: 0.3,
    link: 'https://www.youtube.com/embed/MPc1nuDr1f0',
  },
  {
    title: 'In Vitro Fertilization',
    description: 'How in vitro fertilization (IVF) works - Nassim Assefi and Brian A. Levine',
    thumbnail: ivf1,
    progress: 0.7,
    link: 'https://www.youtube.com/embed/_ijBi93_I94',
  },
  {
    title: 'In Vitro Fertilization',
    description: 'How in vitro fertilization (IVF) works - Nassim Assefi and Brian A. Levine',
    thumbnail: ivf2,
    progress: 0.4,
    link: 'https://www.youtube.com/embed/MPc1nuDr1f0',
  },
];

type VideoType = typeof videos[number];
const PatientEducationVideos: React.FC = () => {
  const { t } = useTranslation('Packagebilling');
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(false);
  const [selectedVideo, setSelectedVideo] = useState<VideoType | null>(null);
  const filteredVideos = videos.filter(v =>
    v.title.toLowerCase().includes(search.toLowerCase()) ||
    v.description.toLowerCase().includes(search.toLowerCase())
  );

  const handlePlayClick = (video: VideoType) => {
    setSelectedVideo(video);
    setOpen(true);
  };
  const handleClose = () => {
    setOpen(false);
    setSelectedVideo(null);
  };

  return (
    <Box data-testid="patient-education-container" sx={{ p: 3 }}>
      <Box data-testid="patient-education-header" sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" fontWeight={700} sx={{ flex: 1 }}>
          {t('patient_education.educational_videos', '🎓 Educational Videos')}
        </Typography>
        <TextField
          data-testid="patient-education-search"
          placeholder={t('search', 'Search')}
          size="small"
          sx={{ width: 260 }}
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
      </Box>
      <Grid data-testid="patient-education-grid" container spacing={3}>
        {filteredVideos.map((video, idx) => (
          <Grid item xs={12} sm={6} md={3} key={idx} data-testid={`patient-education-video-${idx}`}>
            <Card sx={{ boxShadow: 0, borderRadius: 3, position: 'relative', mb: 2 }}>
              <Box sx={{ position: 'relative' }}>
                <CardMedia
                  component="img"
                  height="250"
                  image={video.thumbnail}
                  alt={video.title}
                  sx={{ borderRadius: 3 }}
                />
                <IconButton
                  sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    bgcolor: 'rgba(255,255,255,0.8)',
                    p: 1.5,
                  }}
                  onClick={() => handlePlayClick(video)}
                >
                  <PlayCircleOutlineIcon sx={{ fontSize: 40, color: '#1976d2' }} />
                </IconButton>
                <Box
                  sx={{
                    position: 'absolute',
                    left: 0,
                    bottom: 0,
                    width: '100%',
                    height: 6,
                    bgcolor: '#eee',
                  }}
                >
                  <Box
                    sx={{
                      width: `${video.progress * 100}%`,
                      height: '100%',
                      bgcolor: '#ff7043',
                    }}
                  />
                </Box>
              </Box>
              <CardContent sx={{ pb: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                  <Typography variant="h6" fontWeight={700} sx={{ flex: 1, fontSize: 20 }}>
                    {video.title}
                  </Typography>
                  <MuiLink href={video.link} target="_blank" rel="noopener noreferrer" underline="none" sx={{ display: 'flex', alignItems: 'center', ml: 1 }}>
                    <LinkIcon fontSize="small" sx={{ color: '#1976d2' }} />
                    <Typography variant="body2" sx={{ ml: 0.5, color: '#1976d2' }}>{t('patient_education.link', 'Link')}</Typography>
                  </MuiLink>
                </Box>
                <Typography variant="body2" color="text.secondary">
                  {video.description}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
      <Dialog data-testid="patient-education-video-dialog" open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogContent sx={{ p: 0, bgcolor: '#000' }}>
          {selectedVideo && (
            <Box sx={{ position: 'relative', width: '100%', paddingTop: '56.25%' }}>
              <iframe
                src={`${selectedVideo.link}?autoplay=1`}
                title={selectedVideo.title}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                style={{ 
                  position: 'absolute', 
                  top: 0, 
                  left: 0, 
                  width: '100%', 
                  height: '100%', 
                  border: 'none' 
                }}
              />
              <IconButton
                onClick={handleClose}
                sx={{ position: 'absolute', top: 8, right: 8, color: '#fff', zIndex: 2 }}
              >
                &#10005;
              </IconButton>
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default PatientEducationVideos;
